import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  Divider, 
  IconButton,
  Box,
  Avatar,
  Grid,
  Button,
  Stepper,
  Step,
  StepLabel,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FlagIcon from '@mui/icons-material/Flag';
import { PropTypes } from 'prop-types';
import { useState, useContext, useEffect } from 'react';
import { useBebackApi } from '../hooks/useBebackApi';
import FeedbackContext from '../context/FeedbackContext';
import FeedbackPopUp from './FeedbackPopUp';
import { LyftRideInfo, formatLyftAddress } from '../utils/LyftRide';
import StoreContext from '../context/StoreContext';
import { DateTime } from 'luxon';
import { LYFT_RIDE_STATUSES, LYFT_RIDE_MODAL_STATUS_SECONDS_TO_CLOSE } from '../utils/constants';
import CountdownTimer from './CountdownTimer';
import WarningModal from './WarningModal';

const MR_STAGE = process.env.REACT_APP_MR_STAGE;

function LyftRideStatusDialog({open, onClose, rideInfo, siteNo, isFirstTimeOpen, setIsFirstTimeOpen}) {
  const { cancelRide } = useBebackApi();
  const { setIsLoading } = useContext(FeedbackContext);
  const { time_zone } = useContext(StoreContext);

  const [showPopup, setShowPopup] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState('');
  const [popUpTitle, setPopUpTitle] = useState('');
  const [popUpType, setPopUpType] = useState('');
  const [rideData, setRideData] = useState(LyftRideInfo[rideInfo.status]);
  const [disabledCancelButton, setDisabledCancelButton] = useState(false);
  const [timerProgress, setTimerProgress] = useState(100);
  const [timerLabel, setTimerLabel] = useState('');

  const statusOrder = [
    LYFT_RIDE_STATUSES.pending, 
    LYFT_RIDE_STATUSES.accepted, 
    LYFT_RIDE_STATUSES.arrived, 
    LYFT_RIDE_STATUSES.pickedUp, 
    LYFT_RIDE_STATUSES.droppedOff
  ];
  const rideIndex = statusOrder.indexOf(rideInfo.status);
  const pickedUpIndex = statusOrder.indexOf(LYFT_RIDE_STATUSES.pickedUp);

  const loadRideStatus = () => {
    // 1. Set rideData
    if (LyftRideInfo[rideInfo.status]) {
      setRideData(LyftRideInfo[rideInfo.status]);
    }
  
    // 2. If dropped off, cancel button is disabled
    if (rideInfo.status === LYFT_RIDE_STATUSES.droppedOff) {
      setDisabledCancelButton(true);
      return;
    }
  
    // 3. Handle pickedUp
    if (rideIndex === pickedUpIndex && rideInfo?.pickedUp_event_received_at) {
      const minutesToAdd = ['dev', 'qa'].includes(MR_STAGE) ? 1 : 5;
      const futureTime = DateTime.fromISO(rideInfo.pickedUp_event_received_at)
        .plus({ minutes: minutesToAdd })
        .toUTC()
        .setZone(time_zone);
  
      const now = DateTime.utc();
      const diff = futureTime.diff(now, ['minutes', 'seconds']);
      const remainingMs = diff.toMillis();
  
      // 3a. If timer already expired, update state immediately
      if (remainingMs <= 0) {
        setDisabledCancelButton(true);
        setTimerProgress(0);
        setTimerLabel(`0:00`);
        return;
      }
  
      // 3b. Else, set up countdown
      const rideStatusInterval = setInterval(() => {
        const now = DateTime.utc();
        const diff = futureTime.diff(now, ['minutes', 'seconds']);
        const remainingMs = diff.toMillis();
        const totalMs = minutesToAdd * 60 * 1000;
        const progress = (remainingMs / totalMs) * 100;
  
        const minutes = diff.minutes.toString().padStart(1, '0');
        const seconds = diff.seconds.toFixed(0).toString().padStart(2, '0');
  
        setTimerProgress(Math.round(progress));
        setTimerLabel(`${minutes}:${seconds}`);
  
        if (remainingMs <= 0) {
          console.log('Countdown complete');
          setDisabledCancelButton(true);
          clearInterval(rideStatusInterval);
        }
      }, 1000);
  
      return rideStatusInterval;

    }
  };

  const closeAutomatically = () => {
    return setTimeout(() => {
      setIsFirstTimeOpen(false)
      onClose();
    }, LYFT_RIDE_MODAL_STATUS_SECONDS_TO_CLOSE * 1000);
  }

  useEffect(() => {
    let closeTimeout;
    setIsLoading(true);
    const rideStatusInterval = loadRideStatus();
    setIsLoading(false);
    if(isFirstTimeOpen) {
      closeTimeout = closeAutomatically();
    }
    return () => {
      clearInterval(rideStatusInterval);
      clearTimeout(closeTimeout);
    }
  }, []);
  
  useEffect(() => {
    loadRideStatus();
  }, [rideInfo.status]);

  const onClosePopup = () => {
    setShowPopup(false)
    if(popUpType === 'success') {
      onClose();
    }
  }

  const openCancelModal = () => {
    setShowCancelModal(true)
  }

  const onCloseCancelModal = () =>{
    setShowCancelModal(false)
  }

  const handleCancelRide = async () => {
    try {
      setIsLoading(true);
      onCloseCancelModal()
      
      const reqBody = {
        wo: rideInfo.work_order_id,
        courtesy_ride_id: rideInfo.ride_id,
        siteNo
      }

      const cancelRideRes = await cancelRide(reqBody);
      if(cancelRideRes === undefined){ 
        throw new Error('Service unavailable. Please try again later.');
      }
      setPopUpTitle('Ride canceled!')
      setPopUpMessage('The ride has been canceled')
      setPopUpType('success')
    } catch(err) {
      setPopUpTitle('Something went wrong!')
      setPopUpMessage(err.message)
      setPopUpType('error')
    } finally {
      setIsLoading(false)
      setShowPopup(true)
    }
  }

  const handleGoToLyft = (lyftUrl) => {
    window.open(lyftUrl, "_blank");
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="lyftDialog-title"
      aria-describedby="lyftDialog-description"
      scroll='paper'
      maxWidth='xs'
      fullWidth={true}
    >

      <Divider className='border-yellow border-4'/>

      <DialogTitle id="lyftDialog-title" className='flex justify-between items-center text-base uppercase font-extrabold'>
        Ride status
        <IconButton 
          aria-label='close modal'
          onClick={onClose}
        >
          <CloseIcon className='p-0'/>
        </IconButton>
      </DialogTitle>

      <Divider className='mx-5'/>

      <DialogContent className='w-full'>


        <Box className='w-full flex items-center'>
          <Avatar alt={rideData.title} className='bg-green'>
            {rideData.icon}
          </Avatar>
          <Box className='w-full ml-2 block'>
            <h6 className='text-base font-semibold'>
              {rideData.title}
            </h6>
            <h6 className='text-sm'>
              {rideData.description}
            </h6>
          </Box>
        </Box>

        <Box className='w-full overflow-auto p-2'>
          <Stepper activeStep={rideIndex} alternativeLabel>
            {statusOrder.map((statusKey) => {
              const { icon } = LyftRideInfo[statusKey];
              return (
                <Step key={statusKey}>
                  <StepLabel
                    icon={icon}
                    className={rideIndex >= statusOrder.indexOf(statusKey) ? 'text-green' : ''}
                  />
                </Step>
              );
            })}
          </Stepper>
        </Box>

        <Divider className='my-2' />

        {
          rideInfo?.driver && <>
            <Box className='flex items-center w-full'>
              <Box className='w-3/5'>
                <h6 className='text-base font-semibold'>
                  {rideInfo.vehicle.license_plate}
                </h6>
                <h6 className='text-sm'>
                  {rideInfo.vehicle.make} - {rideInfo.vehicle.model} - {rideInfo.vehicle.color}
                </h6>
                <Box className='mt-1'>
                  <label className='text-sm font-semibold'>{rideInfo.driver.first_name} {rideInfo.driver?.last_name}</label>
                </Box>
              </Box>
              <Box className='w-2/5 h-20 relative'>
                <img src={rideInfo.vehicle.image_url} className='absolute left-0 max-w-full rounded-lg' style={{maxHeight: '70px'}}/>
                <Avatar alt={rideInfo.driver.first_name} src={rideInfo.driver.image_url} className='absolute right-0' sx={{width: 70, height: 70}}/>
              </Box>
            </Box>

            <Divider className='my-2' />

          </>
        }

        <Box className="flex w-full mt-4">
          {/* Icon + line column */}
          <Box className="flex flex-col items-center mr-2">
            <LocationOnIcon className="courtesy-rides-icon-item" />
            <Box className="w-0.5 bg-gray-300 flex-grow my-1" />
            <FlagIcon className="courtesy-rides-icon-item" />
          </Box>

          {/* Address + label column */}
          <Box className="flex flex-col justify-between w-full">
            {/* Pickup row */}
            <Box className="flex items-center justify-between mb-2">
              <span className="flex-grow truncate">{formatLyftAddress(rideInfo.origin.address)}</span>
              <span className="ml-4 text-sm lyft-status-text whitespace-nowrap">Pick up</span>
            </Box>

            {/* Drop-off row */}
            <Box className="flex items-center justify-between">
              <span className="flex-grow truncate">{formatLyftAddress(rideInfo.destination.address)}</span>
              <span className="ml-4 text-sm lyft-status-text whitespace-nowrap">Drop off</span>
            </Box>
          </Box>


        </Box>

        {
          rideInfo?.route_url && (
            <Button variant="contained" size='small' className="button w-32 mt-4 text-xs" onClick={() => handleGoToLyft(rideInfo?.route_url)}>
              More details
            </Button>
          )
        }
        <Divider className='my-2' />

        <Grid container className='mt-4' direction={'row-reverse'}>
          <Grid item xs={12} className='flex mt-auto justify-end'>
            <Grid item xs={5} className='flex items-center justify-start'>
              {
                ((rideIndex === pickedUpIndex) && !disabledCancelButton) && <>
                  <span className='text-xs font-semibold w-28'>Time available to cancel the ride:</span>
                  <CountdownTimer 
                    progress={timerProgress}
                    label={timerLabel}
                  />
                </>
              }
            </Grid>
            <Tooltip title={disabledCancelButton ? "Unable to cancel after 5 minutes of customer pick-up": "Users have up to 5 minutes after customer pick-up to cancel the ride."}>
              <Grid item xs={4}>
                <Button type="submit" variant="contained" className='button red w-full' onClick={openCancelModal} disabled={disabledCancelButton}>
                  Cancel ride
                </Button>
              </Grid>
            </Tooltip>
            <Grid item xs={3} className='pl-1'>
              <Button variant="contained" className='button w-full' onClick={onClose}>
                Close
              </Button>
            </Grid>
          </Grid>
        </Grid>

      </DialogContent>

      {
        showPopup && 
        <FeedbackPopUp
          open={showPopup}
          onClose={onClosePopup}
          title={popUpTitle}
          message={popUpMessage}
          type={popUpType}
        />
      }

      {
        showCancelModal &&
        <WarningModal
          open={showCancelModal}
          onClose={onCloseCancelModal}
          warningText={`Are you sure you want to cancel the ride?`}
          confirmation={handleCancelRide}
        />
      }

    </Dialog>
  )
}

LyftRideStatusDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  rideInfo: PropTypes.object,
  siteNo: PropTypes.string,
  isFirstTimeOpen: PropTypes.bool
};

LyftRideStatusDialog.defaultProps = {
  open: false,
  onClose: () => {},
  rideInfo: {},
  siteNo: '',
  isFirstTimeOpen: false
};

export default LyftRideStatusDialog;